import axios from "axios";

const ENV_BASE = process.env.REACT_APP_BACKEND_URL;
// The preview serves this app on several hostnames (e.g. a job-id alias). Calling the backend on a
// different preview host is cross-origin, and the preview ingress rewrites Origin, so CORS fails.
// When the page is on another preview host, use that same host (it routes /api to this backend).
const resolveBase = () => {
  try {
    const env = new URL(ENV_BASE);
    const here = window.location;
    const isPreview = (h) => h.endsWith(".preview.emergentagent.com");
    return env.origin !== here.origin && isPreview(env.hostname) && isPreview(here.hostname) ? here.origin : ENV_BASE;
  } catch (e) {
    return ENV_BASE;
  }
};
export const API = `${resolveBase()}/api`;
export const api = axios.create({ baseURL: API, withCredentials: true });

// Logged-out reset flow: no cookies, no auth header, form-encoded POSTs -> CORS "simple" requests (no preflight)
export const publicApi = axios.create({ baseURL: API, withCredentials: false });
export const formBody = (obj) => new URLSearchParams(obj);

export const tokens = {
  set: ({ access_token, refresh_token }) => {
    if (access_token) localStorage.setItem("mt_access", access_token);
    if (refresh_token) localStorage.setItem("mt_refresh", refresh_token);
  },
  clear: () => {
    localStorage.removeItem("mt_access");
    localStorage.removeItem("mt_refresh");
  },
};

api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("mt_access");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let refreshing = null;
const NO_RETRY = ["/auth/login", "/auth/register", "/auth/refresh", "/auth/forgot-password", "/auth/reset-password"];

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const orig = error.config;
    if (error.response?.status === 401 && orig && !orig._retry && !NO_RETRY.some((u) => orig.url.includes(u))) {
      orig._retry = true;
      try {
        refreshing =
          refreshing ||
          axios.post(`${API}/auth/refresh`, { refresh_token: localStorage.getItem("mt_refresh") }, { withCredentials: true });
        const { data } = await refreshing;
        refreshing = null;
        tokens.set(data);
        return api(orig);
      } catch (e) {
        refreshing = null;
      }
    }
    return Promise.reject(error);
  }
);

export function errMsg(e) {
  const detail = e?.response?.data?.detail;
  if (detail == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((d) => (d && typeof d.msg === "string" ? d.msg.replace(/^Value error, /, "") : JSON.stringify(d)))
      .join(" ");
  if (typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const EXPENSE_GROUPS = [
  {
    key: "vehicle",
    label: "Vehicle operating costs",
    short: "Vehicle · records only",
    note: "Tracked for your records. Not added on top of the standard mileage deduction.",
    categories: ["Fuel", "Oil", "Maintenance", "Repairs", "Tires", "Insurance", "Registration"],
    badge: "bg-slate-100 text-slate-700 ring-slate-200",
  },
  {
    key: "parking_tolls",
    label: "Business parking & tolls",
    short: "Parking & tolls",
    note: "Business parking and tolls can count in addition to the standard mileage deduction.",
    categories: ["Parking", "Tolls"],
    badge: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  },
  {
    key: "other_business",
    label: "Other business expenses",
    short: "Other business",
    note: "Non-vehicle business costs like equipment and supplies.",
    categories: ["Equipment", "Supplies", "Other"],
    badge: "bg-sky-50 text-sky-800 ring-sky-200",
  },
];
export const CATEGORIES = EXPENSE_GROUPS.flatMap((g) => g.categories);
export const groupOf = (c) => EXPENSE_GROUPS.find((g) => g.categories.includes(c)) || EXPENSE_GROUPS[2];
export const BUSINESS_TYPES = ["Hotshot", "Delivery", "Contractor", "Junk Removal", "Mobile Mechanic", "Other"];

const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
export const fmtMoney = (n) => money.format(Number(n) || 0);
export const fmtMiles = (n) => (Number(n) || 0).toLocaleString("en-US", { maximumFractionDigits: 1 });

export const fmtDate = (s) => {
  if (!s) return "";
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = () => iso(new Date());

export function rangePreset(key) {
  const now = new Date();
  if (key === "month") return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: iso(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
  if (key === "last_month") return { start: iso(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: iso(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (key === "year") return { start: `${now.getFullYear()}-01-01`, end: `${now.getFullYear()}-12-31` };
  return { start: "", end: "" };
}

export async function downloadFile(path, filename) {
  const res = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function startCheckout() {
  const { data } = await api.post("/create-checkout-session", { origin_url: window.location.origin });
  window.location.href = data.url;
}

export async function openBillingPortal() {
  const { data } = await api.post("/billing-portal-session", { origin_url: window.location.origin });
  window.location.href = data.url;
}

export async function openReceipt(id) {
  const win = window.open("", "_blank");
  const res = await api.get(`/receipts/${id}`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  if (win) win.location.href = url;
  else window.location.href = url;
}
